import { supabase } from './supabase'

/**
 * Check if login is allowed (rate limiting)
 */
export const checkLoginAllowed = async (email, ipAddress) => {
  try {
    // Check email rate limit
    const emailCheck = await supabase.rpc('check_rate_limit', {
      action_type_param: 'login',
      identifier_param: email,
      identifier_type_param: 'email',
    })

    if (emailCheck.data && !emailCheck.data.allowed) {
      return {
        allowed: false,
        reason: emailCheck.data.reason,
        message: emailCheck.data.message,
        retryAfter: emailCheck.data.retry_after,
      }
    }

    // Check IP rate limit
    if (ipAddress) {
      const ipCheck = await supabase.rpc('check_rate_limit', {
        action_type_param: 'login',
        identifier_param: ipAddress,
        identifier_type_param: 'ip',
      })

      if (ipCheck.data && !ipCheck.data.allowed) {
        return {
          allowed: false,
          reason: ipCheck.data.reason,
          message: ipCheck.data.message,
          retryAfter: ipCheck.data.retry_after,
        }
      }
    }

    return {
      allowed: true,
      remainingAttempts: emailCheck.data?.remaining_attempts || 5,
    }
  } catch (error) {
    console.error('Error checking login rate limit:', error)
    // Allow login on error to prevent blocking legitimate users
    return { allowed: true, remainingAttempts: 5 }
  }
}

/**
 * Log login attempt
 */
export const logLoginAttempt = async (email, success, failureReason = null, ipAddress = null) => {
  try {
    await supabase.rpc('log_login_attempt', {
      email_param: email,
      ip_address_param: ipAddress,
      user_agent_param: navigator.userAgent,
      success_param: success,
      failure_reason_param: failureReason,
    })
  } catch (error) {
    console.error('Error logging login attempt:', error)
    // Don't throw - logging should not break login
  }
}

/**
 * Get client IP address (would be done server-side in production)
 */
export const getClientIP = async () => {
  try {
    // In production, this would be done server-side
    // For now, we'll use a service or leave null
    // Note: This may be blocked by ad blockers - that's okay
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000) // 3 second timeout
    
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    })
    
    clearTimeout(timeoutId)
    
    if (!response.ok) {
      return null
    }
    
    const data = await response.json()
    return data.ip
  } catch (error) {
    // Silently fail - IP fetching is optional
    return null
  }
}

/**
 * Check if IP is blocked
 */
export const checkIPBlocked = async (ipAddress) => {
  try {
    const { data, error } = await supabase
      .from('ip_blocklist')
      .select('*')
      .eq('ip_address', ipAddress)
      .or(`is_permanent.eq.true,blocked_until.gt.${new Date().toISOString()}`)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return data !== null
  } catch (error) {
    console.error('Error checking IP blocklist:', error)
    return false
  }
}

/**
 * Enhanced login with rate limiting
 */
export const secureLogin = async (email, password) => {
  try {
    // Get client IP
    const ipAddress = await getClientIP()

    // Check if IP is blocked
    if (ipAddress) {
      const isBlocked = await checkIPBlocked(ipAddress)
      if (isBlocked) {
        await logLoginAttempt(email, false, 'IP address is blocked', ipAddress)
        throw new Error('Access denied. Your IP address has been blocked.')
      }
    }

    // Check rate limit
    const rateLimitCheck = await checkLoginAllowed(email, ipAddress)
    if (!rateLimitCheck.allowed) {
      await logLoginAttempt(email, false, rateLimitCheck.reason, ipAddress)
      throw new Error(rateLimitCheck.message)
    }

    // Attempt login
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      await logLoginAttempt(email, false, error.message, ipAddress)
      throw error
    }

    // Log successful login
    await logLoginAttempt(email, true, null, ipAddress)

    return { data, error: null }
  } catch (error) {
    throw error
  }
}

/**
 * Get login attempt history
 */
export const getLoginAttemptHistory = async (email = null, limit = 100) => {
  try {
    let query = supabase
      .from('login_attempts')
      .select('*')
      .order('attempted_at', { ascending: false })
      .limit(limit)

    if (email) {
      query = query.eq('email', email)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching login attempt history:', error)
    throw error
  }
}

